/**
 * SubcribeContent
 * 
 * @Author: Focci
 * @Date: 2023-08-06 14:14:50
 * @Last Modified by: Focci
 * @Last Modified time: 2023-08-06 14:14:50
 */

import PropTypes from 'prop-types'
import ControllerFiled from '@comp/form/ControllerFiled'
import StyledInput from '@comp/form/StyledInput'
import StyledSelect from '@comp/form/StyledSelect'
import ActionButtons from '@comp/form/ActionButtons'
import useMessage from '@hook/useMessage'
import useLogin from '@hook/useLogin'
import { useForm } from 'react-hook-form'
import { fetchPost, fetchProxy } from '@lib/fetch/client'
import { args } from '@lib/parse'
import { useCallback, useState } from 'react'
import { useTranslations } from 'next-intl'
import SubcribeFilterItems from './SubcribeFilterItems'

function SubcribeFormItemScoff({
  label = '',
  required = false,
  children
}) {
  return (
    <div>
      <b className="text-c18">
        {required && <span className="text-error mr-1">*</span>}
        {label}
      </b>
      <div className="mt-2">
        {children}
      </div>
    </div>
  )
}

function SubcribeForm({
  type = '',
  options = {},
  onSuccess,
}) {
  const ts = useTranslations('search')
  const th = useTranslations('house')
  const tp = useTranslations('profile')
  
  const form = useForm()
  const { user } = useLogin()
  const { message } = useMessage()
  const [loading, setLoading] = useState(false)
  
  const perOptions = [
    { label: th('perDay'), value: '1' },
    { label: th('threeDays'), value: '3' },
    { label: th('perWeek'), value: '2' },
    { label: tp('noReceive'), value: '0' },
  ]

  const handleSubmit = useCallback(async (data) => {
    const param = {
      ...args.listingApi(type, options),
      ...data
    }

    await fetchProxy(
      fetchPost('user/criteria', param),
      setLoading
    )
      .then(() => {
        message(ts('subcribeSuccessfully'))
        onSuccess()
      })
      .catch((err) => message(err.message))
  }, [message, onSuccess, options, ts, type])

  return (
    <div className="mt-4 flex flex-col gap-4">
      <SubcribeFormItemScoff label={ts('subcribeName')} required>
        <ControllerFiled
          name="name"
          inputProps={{ variant: 'outline' }}
          Input={StyledInput}
          form={form}
          rules={{ required: 'Name is required' }}
        />
      </SubcribeFormItemScoff>
      <SubcribeFormItemScoff label={ts('yourEmail')} required>
        <ControllerFiled
          name="email"
          defaultValue={user?.email || ''}
          inputProps={{ variant: 'outline' }}
          Input={StyledInput}
          form={form}
          rules={{ 
            required: 'Email is required',
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
              message: 'Invalid email address',
            }
          }}
        />
      </SubcribeFormItemScoff>
      <SubcribeFormItemScoff label={ts('receivedTime')}>
        <ControllerFiled
          name="emailAlert"
          defaultValue={perOptions[0].value}
          inputProps={{ options: perOptions }}
          Input={StyledSelect}
          form={form}
        />
      </SubcribeFormItemScoff>
      <div className="text-center mt-4">
        {ts('subcribeMeta')}
      </div>
      <div className="my-4 mx-auto w-[20rem] max-w-full">
        <ActionButtons 
          loading={loading}
          onConfirm={form.handleSubmit(handleSubmit)}
          onCancel={() => form.reset()}
        />
      </div>
    </div>
  )
}

export default function SubcribeContent({
  type = '',
  options = {},
  onSuccess = () => {},
}) {
  return (
    <div className="max-w-[30rem] mx-auto">
      <div className="flex flex-wrap gap-1 mt-6">
        <SubcribeFilterItems
          type={type}
          closable={false}
          variant="outline"
          searchParam={options}
        />
      </div>
      <SubcribeForm 
        type={type} 
        options={options}
        onSuccess={onSuccess}
      />
    </div>
  )
}

SubcribeContent.propTypes = {
  type: PropTypes.string,
  options: PropTypes.object,
}
